import React, { useState, useEffect, useCallback, useRef } from 'react';
import { GoogleMap, Marker } from '@react-google-maps/api';
import { Menu, Search, Clock, Navigation2, X, Check } from 'lucide-react';
import { getUnifiedCurrentPosition } from '../../services/geolocationService';
import { calculateEstimatedPrice } from '../../services/rideService';
import { toast } from '../../hooks/use-toast';
import RideSelectionModal from './RideSelectionModal';
import ScheduleRideModal from './ScheduleRideModal';
import { useGoogleMaps } from '../maps/GoogleMapsProvider';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const mapContainerStyle = {
  width: '100%',
  height: '100vh'
};

const defaultCenter = {
  lat: -13.9833,
  lng: 33.7833
};

// Google Maps Dark Mode Styles
const darkModeStyles = [
  { elementType: "geometry", stylers: [{ color: "#242f3e" }] },
  { elementType: "labels.text.stroke", stylers: [{ color: "#242f3e" }] },
  { elementType: "labels.text.fill", stylers: [{ color: "#746855" }] },
  { featureType: "administrative.locality", elementType: "labels.text.fill", stylers: [{ color: "#d59563" }] },
  { featureType: "poi", elementType: "labels.text.fill", stylers: [{ color: "#d59563" }] },
  { featureType: "poi.park", elementType: "geometry", stylers: [{ color: "#263c3f" }] },
  { featureType: "poi.park", elementType: "labels.text.fill", stylers: [{ color: "#6b9a76" }] },
  { featureType: "road", elementType: "geometry", stylers: [{ color: "#38414e" }] },
  { featureType: "road", elementType: "geometry.stroke", stylers: [{ color: "#212a37" }] },
  { featureType: "road", elementType: "labels.text.fill", stylers: [{ color: "#9ca5b3" }] },
  { featureType: "road.highway", elementType: "geometry", stylers: [{ color: "#746855" }] },
  { featureType: "road.highway", elementType: "geometry.stroke", stylers: [{ color: "#1f2835" }] },
  { featureType: "road.highway", elementType: "labels.text.fill", stylers: [{ color: "#f3d19c" }] },
  { featureType: "transit", elementType: "geometry", stylers: [{ color: "#2f3948" }] },
  { featureType: "transit.station", elementType: "labels.text.fill", stylers: [{ color: "#d59563" }] },
  { featureType: "water", elementType: "geometry", stylers: [{ color: "#17263c" }] },
  { featureType: "water", elementType: "labels.text.fill", stylers: [{ color: "#515c6d" }] },
  { featureType: "water", elementType: "labels.text.stroke", stylers: [{ color: "#17263c" }] }
];

const getMapOptions = (isDarkMode) => ({
  disableDefaultUI: true,
  zoomControl: false,
  mapTypeControl: false,
  streetViewControl: false,
  fullscreenControl: false,
  clickableIcons: false,
  gestureHandling: 'greedy',
  styles: isDarkMode ? darkModeStyles : []
});

const YachanguBookingPage = ({ user, onMenuClick, onConfirmRide }) => {
  const { isLoaded: mapsLoaded, loadError: mapsLoadError } = useGoogleMaps();
  const [map, setMap] = useState(null);
  const [center, setCenter] = useState(defaultCenter);
  const [pickupLocation, setPickupLocation] = useState(null);
  const [dropoffLocation, setDropoffLocation] = useState(null);
  const [isLoadingLocation, setIsLoadingLocation] = useState(true);
  const [showDestinationSearch, setShowDestinationSearch] = useState(false);
  const [showConfirmPanel, setShowConfirmPanel] = useState(false);
  const [showRideSelectionModal, setShowRideSelectionModal] = useState(false);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [destination, setDestination] = useState(null);
  const [rideEstimates, setRideEstimates] = useState([]);
  const [isCalculatingPrices, setIsCalculatingPrices] = useState(false);
  const [scheduledTime, setScheduledTime] = useState(null);
  const [routePath, setRoutePath] = useState([]);
  const [routeVersion, setRouteVersion] = useState(0); // Force Polyline remount
  const [showRoute, setShowRoute] = useState(false); // Control route visibility
  const searchInputRef = useRef(null);
  const autocompleteRef = useRef(null);
  const activeRouteRequestRef = useRef(null); // Track active route requests
  const polylineRef = useRef(null); // Manual control of Google Maps Polyline
  const [markerPosition, setMarkerPosition] = useState(null);
  const [pickupAddress, setPickupAddress] = useState('Fetching location...');
  const [dropoffAddress, setDropoffAddress] = useState('Selected location');
  const [paymentMethod, setPaymentMethod] = useState('mobile_money'); // 'mobile_money' | 'cash'
  const [hasActiveDiscount, setHasActiveDiscount] = useState(false);
  const [activeDiscountInfo, setActiveDiscountInfo] = useState(null); // { discount_percent, source }
  const [familyCreditBalance, setFamilyCreditBalance] = useState(0); // Balance for primary's family credits
  const [applyFamilyCredit, setApplyFamilyCredit] = useState(false);
  const [personalCreditBalance, setPersonalCreditBalance] = useState(0);
  const [applyPersonalCredit, setApplyPersonalCredit] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(
    document.documentElement.classList.contains('dark')
  );

  // Detect dark mode changes
  useEffect(() => {
    const observer = new MutationObserver(() => {
      const darkModeActive = document.documentElement.classList.contains('dark');
      setIsDarkMode(darkModeActive);
      // Update map styles when theme changes
      if (map) {
        map.setOptions(getMapOptions(darkModeActive));
      }
    });
    
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class']
    });
    
    return () => observer.disconnect();
  }, [map]);

  // Get current location on mount
  useEffect(() => {
    getCurrentLocation();
  }, []);

  // Load user's saved payment preference and active discount eligibility
  useEffect(() => {
    if (!user?.id) return;

    // Saved payment preference (defaults to 'mobile_money')
    fetch(`${API_URL}/api/settings/payments/${user.id}`)
      .then(r => r.json())
      .then(data => {
        if (data?.success && data?.preferences?.selected_payment) {
          const sp = data.preferences.selected_payment;
          // Treat any 'mobile_*' selection as mobile_money for the booking toggle
          setPaymentMethod(sp === 'cash' ? 'cash' : 'mobile_money');
        }
      })
      .catch(() => {});

    // Active referral credits
    fetch(`${API_URL}/api/referrals/credits/${user.id}`)
      .then(r => r.json())
      .then(data => {
        if (data?.success && data?.has_credits && data?.best_credit) {
          setHasActiveDiscount(true);
          setActiveDiscountInfo({
            discount_percent: data.best_credit.discount_percent || 0,
            source: 'referral'
          });
        }
      })
      .catch(() => {});

    // Family credit balance (only relevant for the primary account holder, but harmless to query)
    fetch(`${API_URL}/api/family/${user.id}/credits`)
      .then(r => r.json())
      .then(data => {
        if (data?.success) setFamilyCreditBalance(data.balance || 0);
      })
      .catch(() => {});

    // Personal wallet credits
    fetch(`${API_URL}/api/credits/${user.id}`)
      .then(r => r.json())
      .then(data => {
        if (data?.success) setPersonalCreditBalance(data.balance || 0);
      })
      .catch(() => {});
  }, [user?.id]);

  // Debug: Log route path changes
  useEffect(() => {
    console.log('📍 Route path changed. Length:', routePath.length, 'Points:', routePath.length > 0 ? routePath.slice(0, 2) : 'empty');
  }, [routePath]);

  const getCurrentLocation = async () => {
    setIsLoadingLocation(true);
    try {
      const position = await getUnifiedCurrentPosition();
      const coords = {
        lat: position.coords.latitude,
        lng: position.coords.longitude
      };
      setCenter(coords);
      setPickupLocation(coords);
      setMarkerPosition(coords);
      
      // Reverse geocode to get address
      if (window.google) {
        const geocoder = new window.google.maps.Geocoder();
        geocoder.geocode({ location: coords }, (results, status) => {
          if (status === 'OK' && results[0]) {
            setPickupAddress(results[0].formatted_address);
          } else {
            setPickupAddress('Current Location');
          }
        });
      }
    } catch (error) {
      console.error('Geolocation error:', error);
      
      // User-friendly error handling based on error type
      let errorMessage = 'Unable to get your location';
      let errorDescription = 'Using default location instead';
      
      if (error.code === 1) {
        // PERMISSION_DENIED
        errorMessage = 'Location Access Denied';
        errorDescription = 'Please enable location services in your browser settings to use your current location';
      } else if (error.code === 2) {
        // POSITION_UNAVAILABLE
        errorMessage = 'Location Unavailable';
        errorDescription = 'Your device location is currently unavailable. Please check your GPS settings';
      } else if (error.code === 3) {
        // TIMEOUT
        errorMessage = 'Location Request Timed Out';
        errorDescription = 'It took too long to get your location. Please try again';
      }
      
      // Show toast notification to user
      toast({
        title: errorMessage,
        description: errorDescription,
        variant: 'destructive',
      });
      
      // Fallback to default location (Lilongwe, Malawi)
      setCenter(defaultCenter);
      setPickupLocation(defaultCenter);
      setMarkerPosition(defaultCenter);
      setPickupAddress('Default Location (Lilongwe)');
    } finally {
      setIsLoadingLocation(false);
    }
  };

  const onMapLoad = useCallback((mapInstance) => {
    setMap(mapInstance);
  }, []);

  // Manual Polyline control functions
  const createPolyline = useCallback((path) => {
    if (!map || !window.google) return;
    
    // Remove existing polyline first
    if (polylineRef.current) {
      polylineRef.current.setMap(null);
      polylineRef.current = null;
    }
    
    // Create new polyline
    if (path && path.length > 0) {
      polylineRef.current = new window.google.maps.Polyline({
        path: path,
        geodesic: true,
        strokeColor: '#16A34A',
        strokeOpacity: 0.8,
        strokeWeight: 5,
        map: map
      });
      console.log('✅ Polyline created with', path.length, 'points');
    }
  }, [map]);

  const clearPolyline = useCallback(() => {
    console.log('🧹 Clearing polyline');
    if (polylineRef.current) {
      polylineRef.current.setMap(null);
      polylineRef.current = null;
      console.log('✅ Polyline removed from map');
    }
  }, []);

  const handleWhereToClick = () => {
    setShowDestinationSearch(true);
  };

  const handleLaterClick = () => {
    setShowScheduleModal(true);
  };

  const handleScheduleConfirm = (scheduleDateTime) => {
    setScheduledTime(scheduleDateTime);
    setShowScheduleModal(false);
  };

  // Handle map click to set destination
  const handleMapClick = useCallback((e) => {
    if (showDestinationSearch || showRideSelectionModal || showConfirmPanel || !pickupLocation) return;
    
    const clickedLocation = {
      lat: e.latLng.lat(),
      lng: e.latLng.lng()
    };
    
    setDropoffLocation(clickedLocation);
    
    // Reverse geocode to get address
    if (window.google) {
      const geocoder = new window.google.maps.Geocoder();
      geocoder.geocode({ location: clickedLocation }, (results, status) => {
        if (status === 'OK' && results[0]) {
          const address = results[0].formatted_address;
          setDropoffAddress(address);
          setDestination({
            ...clickedLocation,
            address: address
          });
        } else {
          setDropoffAddress(`${clickedLocation.lat.toFixed(5)}, ${clickedLocation.lng.toFixed(5)}`);
          setDestination({
            ...clickedLocation,
            address: `${clickedLocation.lat.toFixed(5)}, ${clickedLocation.lng.toFixed(5)}`
          });
        }
      });
    } else {
      setDropoffAddress(`${clickedLocation.lat.toFixed(5)}, ${clickedLocation.lng.toFixed(5)}`);
      setDestination({
        ...clickedLocation,
        address: `${clickedLocation.lat.toFixed(5)}, ${clickedLocation.lng.toFixed(5)}`
      });
    }
    
    setShowConfirmPanel(true);
    
    // Calculate route with request tracking
    if (window.google && pickupLocation) {
      const requestId = Date.now();
      activeRouteRequestRef.current = requestId;
      console.log('🗺️ Starting route calculation, requestId:', requestId);
      
      const directionsService = new window.google.maps.DirectionsService();
      directionsService.route(
        {
          origin: pickupLocation,
          destination: clickedLocation,
          travelMode: window.google.maps.TravelMode.DRIVING,
        },
        (result, status) => {
          // Only update if this is still the active request
          if (activeRouteRequestRef.current === requestId) {
            if (status === 'OK') {
              const path = result.routes[0].overview_path.map(p => ({
                lat: p.lat(),
                lng: p.lng()
              }));
              console.log('✅ Route calculation complete, setting path');
              setRoutePath(path);
              setRouteVersion(prev => prev + 1);
              setShowRoute(true);
              createPolyline(path); // Manually create polyline
            }
          } else {
            console.log('⏭️ Ignoring stale route calculation (request was cancelled)');
          }
        }
      );
    }
  }, [pickupLocation, showDestinationSearch, showRideSelectionModal, showConfirmPanel]);

  const handleDestinationSelected = useCallback(async (place) => {
    if (!place.geometry) return;

    const dest = {
      lat: place.geometry.location.lat(),
      lng: place.geometry.location.lng(),
      address: place.formatted_address
    };

    setDestination(dest);
    setDropoffLocation(dest);
    setDropoffAddress(place.formatted_address);
    setShowDestinationSearch(false);
    setShowConfirmPanel(true);
    
    // Calculate route with request tracking
    if (window.google && pickupLocation) {
      const requestId = Date.now();
      activeRouteRequestRef.current = requestId;
      
      const directionsService = new window.google.maps.DirectionsService();
      directionsService.route(
        {
          origin: pickupLocation,
          destination: dest,
          travelMode: window.google.maps.TravelMode.DRIVING,
        },
        (result, status) => {
          // Only update if this is still the active request
          if (activeRouteRequestRef.current === requestId && status === 'OK') {
            const path = result.routes[0].overview_path.map(p => ({
              lat: p.lat(),
              lng: p.lng()
            }));
            setRoutePath(path);
            setRouteVersion(prev => prev + 1);
            setShowRoute(true);
            createPolyline(path); // Manually create polyline
          }
        }
      );
    }
  }, [pickupLocation]);

  // Initialize Google Places Autocomplete
  useEffect(() => {
    if (showDestinationSearch && searchInputRef.current && window.google && !autocompleteRef.current) {
      autocompleteRef.current = new window.google.maps.places.Autocomplete(
        searchInputRef.current,
        {
          componentRestrictions: { country: 'mw' },
          fields: ['formatted_address', 'geometry', 'name']
        }
      );
      autocompleteRef.current.addListener('place_changed', () => {
        const place = autocompleteRef.current.getPlace();
        if (place) {
          handleDestinationSelected(place);
        }
      });
    }
  }, [showDestinationSearch, handleDestinationSelected]);

  const handleRecenterClick = () => {
    if (map && pickupLocation) {
      map.panTo(pickupLocation);
      map.setZoom(15);
      setMarkerPosition(pickupLocation);
    }
  };

  const handlePickupMarkerDragEnd = (e) => {
    const newPos = {
      lat: e.latLng.lat(),
      lng: e.latLng.lng()
    };
    setMarkerPosition(newPos);
    setPickupLocation(newPos);
    
    // Recalculate route if destination exists
    if (dropoffLocation && window.google) {
      const requestId = Date.now();
      activeRouteRequestRef.current = requestId;
      
      const directionsService = new window.google.maps.DirectionsService();
      directionsService.route(
        {
          origin: newPos,
          destination: dropoffLocation,
          travelMode: window.google.maps.TravelMode.DRIVING,
        },
        (result, status) => {
          if (activeRouteRequestRef.current === requestId && status === 'OK') {
            const path = result.routes[0].overview_path.map(p => ({
              lat: p.lat(),
              lng: p.lng()
            }));
            setRoutePath(path);
            setRouteVersion(prev => prev + 1);
            setShowRoute(true);
            createPolyline(path); // Manually create polyline
          }
        }
      );
    }
  };

  const handleDropoffMarkerDragEnd = (e) => {
    const newPos = {
      lat: e.latLng.lat(),
      lng: e.latLng.lng()
    };
    setDropoffLocation(newPos);
    
    // Reverse geocode for dragged position
    if (window.google) {
      const geocoder = new window.google.maps.Geocoder();
      geocoder.geocode({ location: newPos }, (results, status) => {
        if (status === 'OK' && results[0]) {
          const address = results[0].formatted_address;
          setDropoffAddress(address);
          setDestination({
            ...newPos,
            address: address
          });
        } else {
          setDropoffAddress(`${newPos.lat.toFixed(5)}, ${newPos.lng.toFixed(5)}`);
          setDestination({
            ...newPos,
            address: `${newPos.lat.toFixed(5)}, ${newPos.lng.toFixed(5)}`
          });
        }
      });
    }
    
    // Recalculate route
    if (pickupLocation && window.google) {
      const requestId = Date.now();
      activeRouteRequestRef.current = requestId;
      
      const directionsService = new window.google.maps.DirectionsService();
      directionsService.route(
        {
          origin: pickupLocation,
          destination: newPos,
          travelMode: window.google.maps.TravelMode.DRIVING,
        },
        (result, status) => {
          if (activeRouteRequestRef.current === requestId && status === 'OK') {
            const path = result.routes[0].overview_path.map(p => ({
              lat: p.lat(),
              lng: p.lng()
            }));
            setRoutePath(path);
            setRouteVersion(prev => prev + 1);
            setShowRoute(true);
            createPolyline(path); // Manually create polyline
          }
        }
      );
    }
  };

  const handleConfirmDropoff = async () => {
    if (!pickupLocation || !destination) {
      toast({
        title: 'Error',
        description: 'Please ensure both pickup and dropoff locations are set',
        variant: 'destructive'
      });
      return;
    }

    setShowConfirmPanel(false);
    setIsCalculatingPrices(true);
    setShowRideSelectionModal(true);

    try {
      const rideTypes = ['economy', 'premium', 'bike'];
      const estimates = await Promise.all(
        rideTypes.map(async (vehicleType) => {
          const estimate = await calculateEstimatedPrice(
            { lat: pickupLocation.lat, lng: pickupLocation.lng, address: 'Current Location' },
            destination,
            vehicleType
          );
          
          // Distance is at the top level, not in breakdown
          const distanceKm = estimate.distance || 0;
          const etaMinutes = estimate.eta || Math.round((distanceKm / 30) * 60);
          const finalEta = Math.max(etaMinutes, 5);
          
          console.log(`Estimate for ${vehicleType}:`, { distance: distanceKm, price: estimate.estimatedPrice, eta: finalEta });
          
          return {
            vehicle_type: vehicleType,
            price: estimate.estimatedPrice,
            distance: distanceKm,
            eta: finalEta,
            breakdown: estimate.breakdown
          };
        })
      );
      setRideEstimates(estimates);
    } catch (error) {
      console.error('Price calculation error:', error);
      toast({
        title: 'Error',
        description: 'Failed to calculate prices',
        variant: 'destructive'
      });
      setShowRideSelectionModal(false);
    } finally {
      setIsCalculatingPrices(false);
    }
  };

  const handleClearDestination = () => {
    console.log('🧹 Clearing destination and route');
    console.log('Current routePath length:', routePath.length);
    console.log('Current dropoffLocation:', dropoffLocation);
    
    // Cancel any pending route calculations
    activeRouteRequestRef.current = null;
    
    // MANUALLY remove polyline from map
    clearPolyline();
    
    // FIRST hide the route
    setShowRoute(false);
    
    setDropoffLocation(null);
    setDestination(null);
    setRoutePath([]);
    setRouteVersion(prev => prev + 1);
    setShowConfirmPanel(false);
    setShowRideSelectionModal(false);
    setRideEstimates([]);
    setDropoffAddress('Selected location');
    setShowDestinationSearch(false);
    
    // Re-center map to pickup location
    if (map && pickupLocation) {
      map.panTo(pickupLocation);
      map.setZoom(15);
    }
    
    console.log('✅ Destination cleared, route manually removed, state reset');
  };

  const handleRideConfirmed = (selectedEstimate) => {
    const rideData = {
      pickup: pickupLocation,
      destination: destination,
      vehicleType: selectedEstimate.vehicle_type,
      estimatedPrice: selectedEstimate.price,
      distance: selectedEstimate.distance,
      scheduledTime: scheduledTime,
      paymentMethod: paymentMethod,
      applyFamilyCredit: applyFamilyCredit,
      applyPersonalCredit: applyPersonalCredit
    };
    onConfirmRide(rideData);
    // Reset state — fully clear the booking UI so it returns to the empty map
    // (important for scheduled rides, which don't open the allocation modal).
    setScheduledTime(null);
    setDestination(null);
    setDropoffLocation(null);
    setRoutePath([]);
    setShowConfirmPanel(false);
    setShowRideSelectionModal(false);
    setShowDestinationSearch(false);
    setDropoffAddress('Selected location');
  };

  return (
    <div className="relative w-full h-screen overflow-hidden">
      {/* Loading State */}
      {isLoadingLocation && (
        <div className="absolute inset-0 bg-white z-50 flex items-center justify-center">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-green-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-lg text-gray-700 font-semibold">Loading your location...</p>
          </div>
        </div>
      )}

      {/* Full-Screen Google Map */}
      {mapsLoaded ? (
        <GoogleMap
          mapContainerStyle={mapContainerStyle}
          center={center}
          zoom={15}
          onLoad={onMapLoad}
          options={getMapOptions(isDarkMode)}
          onClick={handleMapClick}
        >
          {/* Route Polyline - Now manually controlled via polylineRef */}
          {/* Polyline is created/removed via native Google Maps API in createPolyline/clearPolyline */}

          {/* Draggable Marker for Pickup Location - Green Pin */}
          {markerPosition && (
            <Marker
              position={markerPosition}
              draggable={true}
              onDragEnd={handlePickupMarkerDragEnd}
              icon={{
                path: 'M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z',
                fillColor: '#16A34A',
                fillOpacity: 1,
                strokeColor: '#ffffff',
                strokeWeight: 2,
                scale: 2,
                anchor: window.google?.maps ? new window.google.maps.Point(12, 22) : null
              }}
            />
          )}

          {/* Draggable Dropoff Location Marker - Red Pin */}
          {dropoffLocation && (
            <Marker
              position={dropoffLocation}
              draggable={true}
              onDragEnd={handleDropoffMarkerDragEnd}
              icon={{
                path: 'M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z',
                fillColor: '#EF4444',
                fillOpacity: 1,
                strokeColor: '#ffffff',
                strokeWeight: 2,
                scale: 2,
                anchor: window.google?.maps ? new window.google.maps.Point(12, 22) : null
              }}
            />
          )}
        </GoogleMap>
      ) : (
        /* Maps unavailable fallback — keeps the booking screen usable */
        <div
          className="absolute inset-0 bg-gradient-to-b from-emerald-50 to-gray-100 flex items-center justify-center"
          data-testid="booking-map-fallback"
        >
          <div className="text-center px-8 max-w-xs">
            <div className="w-14 h-14 rounded-2xl bg-emerald-100 mx-auto mb-4 flex items-center justify-center">
              <Navigation2 className="w-7 h-7 text-emerald-600" />
            </div>
            <p className="text-base font-semibold text-gray-800 mb-1">
              {mapsLoadError ? 'Map preview unavailable' : 'Loading map…'}
            </p>
            <p className="text-sm text-gray-500">
              {mapsLoadError
                ? 'You can still set your destination using the search box below.'
                : 'Getting things ready.'}
            </p>
          </div>
        </div>
      )}

      {/* Top Bar with Menu and Search */}
      <div className="absolute top-0 left-0 right-0 p-4 z-20 flex items-center space-x-3">
        <button
          onClick={onMenuClick}
          className="w-12 h-12 bg-white rounded-full shadow-lg flex items-center justify-center active:scale-95 transition-all"
        >
          <Menu className="w-6 h-6 text-gray-800" />
        </button>
        
        {/* Search Button */}
        {!showDestinationSearch && !showConfirmPanel && (
          <div className="flex-1 flex items-center space-x-2">
            <button
              onClick={handleWhereToClick}
              className="flex-1 bg-white hover:bg-green-50 rounded-full h-12 px-4 shadow-lg flex items-center space-x-3 transition-all active:scale-[0.98]"
            >
              <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                <Search className="w-5 h-5 text-green-700" />
              </div>
              <span className="text-base text-gray-800 font-medium flex-1 text-left">
                Where to?
              </span>
            </button>
            <button
              onClick={handleLaterClick}
              className="flex items-center space-x-1 bg-white hover:bg-green-50 px-3 h-12 rounded-full shadow-lg flex-shrink-0 transition-all active:scale-95"
            >
              <Clock className="w-4 h-4 text-green-600" />
              <span className="text-xs text-green-700 font-medium">Later</span>
            </button>
          </div>
        )}
        
        {/* Clear destination button */}
        {(showConfirmPanel || dropoffLocation) && !showDestinationSearch && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleClearDestination();
            }}
            className="w-12 h-12 bg-white rounded-full shadow-lg flex items-center justify-center active:scale-95 transition-all hover:bg-red-50"
          >
            <X className="w-6 h-6 text-red-600" />
          </button>
        )}
      </div>

      {/* Re-center Button */}
      {!showDestinationSearch && !showConfirmPanel && (
        <button
          onClick={handleRecenterClick}
          className="absolute bottom-28 right-4 w-12 h-12 bg-white rounded-full shadow-lg flex items-center justify-center z-20 active:scale-95 transition-transform hover:bg-green-50"
        >
          <Navigation2 className="w-6 h-6 text-gray-700" />
        </button>
      )}

      {/* Destination Search Modal */}
      {showDestinationSearch && (
        <div className="absolute inset-0 bg-black/20 backdrop-blur-sm z-30">
          <div className="bg-white rounded-b-3xl shadow-lg">
            <div className="p-4">
              <div className="flex items-center space-x-3 mb-4">
                <button
                  onClick={() => {
                    setShowDestinationSearch(false);
                    setScheduledTime(null);
                  }}
                  className="w-10 h-10 flex items-center justify-center hover:bg-gray-100 rounded-full transition-colors"
                >
                  <span className="text-2xl text-gray-800 dark:text-white">←</span>
                </button>
                <input
                  ref={searchInputRef}
                  type="text"
                  placeholder={scheduledTime ? `Schedule for ${scheduledTime.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}` : 'Where to?'}
                  className="flex-1 text-lg py-3 px-4 bg-green-50 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500"
                  autoFocus
                />
              </div>
              {scheduledTime && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-3">
                  <p className="text-sm text-blue-900">
                    <strong>📅 Scheduled:</strong> {scheduledTime.toLocaleString('en-US', { 
                      weekday: 'short',
                      month: 'short', 
                      day: 'numeric', 
                      hour: '2-digit', 
                      minute: '2-digit' 
                    })}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Confirm Dropoff Panel - Compact & Floating Above Navigation */}
      {showConfirmPanel && dropoffLocation && pickupLocation && (
        <div className="fixed bottom-36 left-1/2 transform -translate-x-1/2 z-40 w-80 max-w-[90vw]">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl p-4 border border-gray-200 dark:border-gray-700 transition-colors">
            {/* Pickup & Dropoff - Compact */}
            <div className="space-y-2 mb-3">
              {/* Pickup Row */}
              <div className="flex items-start space-x-2 text-xs">
                <div className="w-2 h-2 bg-green-600 dark:bg-green-500 rounded-full flex-shrink-0 mt-1"></div>
                <div className="flex-1 min-w-0">
                  <span className="text-gray-500 dark:text-gray-400 font-medium">From: </span>
                  <span className="text-gray-800 dark:text-white break-words">{pickupAddress}</span>
                </div>
              </div>

              {/* Dropoff Row */}
              <div className="flex items-start space-x-2 text-xs">
                <div className="w-2 h-2 bg-red-600 dark:bg-red-500 rounded-full flex-shrink-0 mt-1"></div>
                <div className="flex-1 min-w-0">
                  <span className="text-gray-500 dark:text-gray-400 font-medium">To: </span>
                  <span className="text-gray-800 dark:text-white break-words">{dropoffAddress}</span>
                </div>
              </div>
            </div>

            {/* Confirm Button - Compact */}
            <div className="flex gap-2">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleClearDestination();
                }}
                className="w-10 h-10 flex items-center justify-center border border-gray-300 dark:border-gray-600 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors flex-shrink-0"
              >
                <X className="w-5 h-5 text-gray-600 dark:text-gray-400" />
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleConfirmDropoff();
                }}
                className="flex-1 bg-green-600 hover:bg-green-700 text-white py-2.5 rounded-xl font-semibold text-sm flex items-center justify-center space-x-1.5 transition-colors"
              >
                <Check className="w-4 h-4" />
                <span>Confirm</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Ride Selection Modal - Regular size */}
      <RideSelectionModal
        isOpen={showRideSelectionModal}
        onClose={() => {
          setShowRideSelectionModal(false);
          // Clear the route and destination when modal is closed/cancelled
          handleClearDestination();
        }}
        estimates={rideEstimates}
        pickupLocation={pickupLocation}
        onConfirmRide={handleRideConfirmed}
        isLoading={isCalculatingPrices}
        compact={false}
        paymentMethod={paymentMethod}
        onPaymentMethodChange={setPaymentMethod}
        hasActiveDiscount={hasActiveDiscount}
        activeDiscountInfo={activeDiscountInfo}
        familyCreditBalance={familyCreditBalance}
        applyFamilyCredit={applyFamilyCredit}
        onApplyFamilyCreditChange={setApplyFamilyCredit}
        personalCreditBalance={personalCreditBalance}
        applyPersonalCredit={applyPersonalCredit}
        onApplyPersonalCreditChange={setApplyPersonalCredit}
      />

      {/* Schedule Ride Modal */}
      <ScheduleRideModal
        isOpen={showScheduleModal}
        onClose={() => setShowScheduleModal(false)}
        onSchedule={handleScheduleConfirm}
      />
      
      {/* Instruction Hint */}
      {!dropoffLocation && !showDestinationSearch && !showConfirmPanel && (
        <div className="absolute bottom-32 left-1/2 transform -translate-x-1/2 bg-green-600 text-white px-4 py-2 rounded-full shadow-lg text-sm font-medium animate-bounce z-10">
          👆 Tap anywhere on map to set destination
        </div>
      )}
    </div>
  );
};

export default YachanguBookingPage;